import { useState, type FormEvent } from "react";
import { Link, useNavigate } from "react-router-dom";
import HeroCarousel from "@/pages/home/components/HeroCarousel";
import WorkCard from "@/components/feature/WorkCard";
import WorkListRow from "@/components/feature/WorkListRow";
import { usePageMeta } from "@/hooks/usePageMeta";
import {
  allCategories,
  getFeaturedWorks,
  getLatestWorks,
  getPopularWorks,
} from "@/utils/videoUtils";

export default function Home() {
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");

  usePageMeta(
    "日本伦理剧 - 精选伦理剧情影视内容平台",
    "日本伦理剧网站专注于提供丰富的日本伦理剧影视内容，涵盖多部经典与最新伦理剧情作品，满足用户观看和了解日本伦理戏剧的需求。",
    "日本伦理剧,伦理电视剧,日本伦理电影,伦理剧情,日本影视,伦理影视内容,日本伦理剧情",
  );

  const featured = getFeaturedWorks();
  const latest = getLatestWorks(8);
  const popular = getPopularWorks(6);

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/search?q=${encodeURIComponent(q)}` : "/search");
  };

  return (
    <div className="w-full">
      <HeroCarousel items={featured} />

      {/* 搜索入口 */}
      <section className="w-full px-4 md:px-6 -mt-10 relative z-20">
        <div className="max-w-3xl mx-auto rounded-lg bg-background-50 border border-background-200/70 p-4 md:p-5">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-500 text-lg"></i>
              <input
                type="text"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="输入剧名、导演、题材关键词，快速检索伦理剧情作品"
                aria-label="搜索影视内容"
                className="w-full h-11 pl-10 pr-3 text-sm rounded-md border border-background-300 bg-background-100 text-foreground-900 placeholder:text-foreground-500 focus:outline-none focus:ring-2 focus:ring-primary-300"
              />
            </div>
            <button
              type="submit"
              className="h-11 px-7 rounded-md bg-primary-700 text-background-50 font-label text-sm hover:bg-primary-800 transition-colors cursor-pointer whitespace-nowrap"
            >
              搜索作品
            </button>
          </form>
          <div className="mt-3 flex flex-wrap items-center gap-2 text-xs font-label text-foreground-600">
            <span className="text-foreground-500">热门搜索：</span>
            {["治愈", "家庭", "都市情感", "经典", "女性题材"].map((k) => (
              <Link
                key={k}
                to={`/search?q=${encodeURIComponent(k)}`}
                className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-700 hover:bg-accent-100 hover:text-accent-900 transition-colors"
              >
                {k}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 分类快捷入口 */}
      <section className="w-full px-4 md:px-6 mt-14">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">分类快捷入口</h2>
            <p className="mt-1 text-sm text-foreground-600">按类型快速定位你感兴趣的伦理剧情内容</p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {allCategories.map((cat) => (
            <Link
              key={cat.key}
              to={`/category/${cat.key}`}
              className="group relative rounded-lg overflow-hidden h-44 border border-background-200/70"
            >
              <img
                src={cat.image}
                alt={`${cat.name} 分类`}
                title={`${cat.name} - 日本伦理剧`}
                className="absolute inset-0 w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-primary-950/90 via-primary-950/40 to-primary-950/10"></div>
              <div className="relative z-10 h-full flex flex-col justify-end p-4">
                <span className="w-10 h-10 flex items-center justify-center rounded-md bg-accent-500 text-accent-950 mb-2">
                  <i className={`${cat.icon} text-xl`}></i>
                </span>
                <h3 className="font-heading text-lg font-semibold text-background-50">{cat.name}</h3>
                <p className="mt-1 text-xs text-background-200/85 line-clamp-2">{cat.description}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* 最新上映 */}
      <section className="w-full px-4 md:px-6 mt-16">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">最新上映</h2>
            <p className="mt-1 text-sm text-foreground-600">最近更新与新增的伦理剧情作品</p>
          </div>
          <Link
            to="/category/latest"
            className="hidden sm:inline-flex items-center gap-1 text-sm font-label text-primary-600 hover:text-primary-800 transition-colors"
          >
            查看全部 <i className="ri-arrow-right-s-line"></i>
          </Link>
        </div>
        <div className="flex gap-4 overflow-x-auto pb-2 hide-scrollbar">
          {latest.map((work) => (
            <div key={work.id} className="w-40 md:w-48 shrink-0">
              <WorkCard work={work} />
            </div>
          ))}
        </div>
      </section>

      {/* 热门推荐 */}
      <section className="w-full px-4 md:px-6 mt-16">
        <div className="flex items-end justify-between mb-5">
          <div>
            <h2 className="font-heading text-2xl font-bold text-foreground-950">热门推荐</h2>
            <p className="mt-1 text-sm text-foreground-600">基于访问量统计的热门伦理剧情作品</p>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {popular.map((work, i) => (
            <div key={work.id} className="relative flex gap-3">
              <span
                className={`shrink-0 w-8 text-center font-heading text-3xl font-bold leading-none pt-1 ${
                  i < 3 ? "text-accent-500" : "text-background-400"
                }`}
              >
                {i + 1}
              </span>
              <div className="flex-1 min-w-0">
                <WorkListRow work={work} />
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* 网站简介 */}
      <section className="w-full px-4 md:px-6 mt-16">
        <div className="rounded-lg bg-primary-900 text-background-100 p-6 md:p-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50">关于本站</h2>
              <p className="mt-4 text-sm md:text-base text-background-200/85 leading-relaxed">
                日本伦理剧致力于系统梳理与介绍日本伦理剧情影视内容，从国民级经典到话题新作，涵盖家庭、都市情感、女性题材等多元方向。我们为影迷、研究者与文化爱好者提供清晰的作品信息与贴心的推荐浏览体验，帮助你在众多作品中找到值得一看的那一部。
              </p>
              <Link
                to="/about"
                className="mt-6 inline-flex items-center gap-2 h-11 px-6 rounded-md bg-accent-500 text-accent-950 font-label text-sm hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                了解更多 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { num: "12+", label: "精选影视作品" },
                { num: "4", label: "内容分类" },
                { num: "100%", label: "免费浏览" },
                { num: "每日", label: "内容更新" },
              ].map((stat) => (
                <div key={stat.label} className="rounded-lg bg-primary-800/70 p-4 text-center">
                  <div className="font-heading text-2xl md:text-3xl font-bold text-accent-400">{stat.num}</div>
                  <div className="mt-1 text-xs text-background-200/80 font-label">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}